import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Env, Mode } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import type { Organization, Workspace } from '@/api/discovery';

export interface NavItem {
  id: string;
  label: string;
  description?: string;
  status?: 'ready' | 'beta' | 'soon';
  icon?: ReactNode;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

interface Props {
  navSections: NavSection[];
  activeId: string;
  onSelect: (id: string) => void;
  mode: Mode;
  onModeChange: (m: Mode) => void;
  children: ReactNode;
}

export function PlaygroundShell({ navSections, activeId, onSelect, mode, onModeChange, children }: Props) {
  const {
    env,
    setEnv,
    tokens,
    email,
    organizations,
    workspaces,
    organizationId,
    workspaceId,
    setOrganizationId,
    setWorkspaceId,
    discoveringContext,
    signOut,
  } = useAuth();

  return (
    <div
      className="lp-shell lp-min-h-[760px] lp-flex lp-overflow-hidden lp-shadow-card"
      style={{ borderRadius: 22, border: '1px solid #e5e7eb' }}
    >
      <SidebarNav navSections={navSections} activeId={activeId} onSelect={onSelect} />
      <div className="lp-flex-1 lp-flex lp-flex-col lp-min-w-0">
        <Topbar
          env={env}
          onEnvChange={setEnv}
          mode={mode}
          onModeChange={onModeChange}
          email={email}
          isAuthed={!!tokens}
          onSignOut={signOut}
          organizations={organizations}
          workspaces={workspaces}
          organizationId={organizationId}
          workspaceId={workspaceId}
          onOrganizationIdChange={setOrganizationId}
          onWorkspaceIdChange={setWorkspaceId}
          discovering={discoveringContext}
        />
        <div className="lp-flex-1 lp-overflow-auto lp-p-7" style={{ background: '#f8fafc' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sidebar
// ============================================================================

function SidebarNav({
  navSections,
  activeId,
  onSelect,
}: Pick<Props, 'navSections' | 'activeId' | 'onSelect'>) {
  return (
    <aside
      className="lp-w-56 lp-shrink-0 lp-flex lp-flex-col"
      style={{ background: '#ffffff', borderRight: '1px solid #e5e7eb' }}
    >
      <div className="lp-px-5 lp-pt-6 lp-pb-5" style={{ borderBottom: '1px solid #f1f5f9' }}>
        <div className="lp-flex lp-items-center lp-gap-2.5">
          <span
            className="lp-w-9 lp-h-9 lp-flex lp-items-center lp-justify-center lp-shrink-0"
            style={{
              borderRadius: 10,
              background: 'linear-gradient(135deg, #6256ff, #7c3aed)',
              boxShadow: '0 8px 18px rgba(98, 86, 255, 0.28)',
            }}
          >
            <svg className="lp-w-4 lp-h-4 lp-text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <div>
            <div className="lp-text-[10px] lp-font-bold lp-uppercase" style={{ color: '#6256ff', letterSpacing: '0.12em' }}>
              Libertify
            </div>
            <div className="lp-text-sm lp-font-bold lp-leading-tight" style={{ color: '#0f172a' }}>
              API Playground
            </div>
          </div>
        </div>
      </div>
      <nav className="lp-flex-1 lp-px-3 lp-py-4 lp-space-y-4 lp-overflow-y-auto">
        {navSections.map((section) => (
          <div key={section.id}>
            <div
              className="lp-text-[10px] lp-font-bold lp-uppercase lp-px-3 lp-mb-1.5"
              style={{ letterSpacing: '0.14em', color: '#94a3b8' }}
            >
              {section.label}
            </div>
            <div className="lp-space-y-0.5">
              {section.items.map((item) => {
                const active = item.id === activeId;
                return (
                  <button
                    key={item.id}
                    onClick={() => onSelect(item.id)}
                    className="lp-w-full lp-text-left lp-px-3 lp-py-2 lp-rounded-lg lp-transition-all lp-flex lp-items-center lp-justify-between lp-gap-2 lp-group"
                    style={{
                      background: active ? '#f1f0ff' : 'transparent',
                      color: active ? '#3328aa' : '#334155',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span className="lp-flex lp-items-center lp-gap-2.5 lp-min-w-0">
                      <span
                        aria-hidden
                        style={{
                          width: 3,
                          height: 18,
                          borderRadius: 9999,
                          background: active ? '#6256ff' : 'transparent',
                          transition: 'background-color 0.15s',
                        }}
                      />
                      <span className="lp-text-[13.5px] lp-font-medium lp-truncate">{item.label}</span>
                    </span>
                    {item.status === 'soon' && (
                      <span
                        className="lp-text-[9px] lp-font-bold lp-px-1.5 lp-py-0.5 lp-rounded lp-uppercase lp-tracking-wider"
                        style={{ background: '#f1f5f9', color: '#64748b' }}
                      >
                        Soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div
        className="lp-px-5 lp-py-4 lp-text-[11px] lp-leading-relaxed"
        style={{ borderTop: '1px solid #f1f5f9', color: '#94a3b8' }}
      >
        <a
          href="https://api.libertify.com/media/docs"
          target="_blank"
          rel="noreferrer noopener"
          className="lp-font-medium"
          style={{ color: '#475569' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#6256ff')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
        >
          API reference ↗
        </a>
        <span className="lp-mx-2" style={{ color: '#cbd5e1' }}>·</span>
        <a
          href="https://app.libertify.com"
          target="_blank"
          rel="noreferrer noopener"
          className="lp-font-medium"
          style={{ color: '#475569' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#6256ff')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
        >
          Libertify app ↗
        </a>
      </div>
    </aside>
  );
}

// ============================================================================
// Topbar
// ============================================================================

interface TopbarProps {
  env: Env;
  onEnvChange: (e: Env) => void;
  mode: Mode;
  onModeChange: (m: Mode) => void;
  email?: string;
  isAuthed: boolean;
  onSignOut: () => void;
  organizations: Organization[];
  workspaces: Workspace[];
  organizationId: string;
  workspaceId: string;
  onOrganizationIdChange: (id: string) => void;
  onWorkspaceIdChange: (id: string) => void;
  discovering: boolean;
}

function Topbar(props: TopbarProps) {
  return (
    <header
      className="lp-h-14 lp-flex lp-items-center lp-justify-between lp-px-6 lp-gap-3"
      style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}
    >
      <div className="lp-flex lp-items-center lp-gap-3 lp-min-w-0 lp-flex-1">
        <ModeToggle mode={props.mode} onChange={props.onModeChange} />
        <span style={{ width: 1, height: 24, background: '#e5e7eb' }} />
        <EnvSwitcher env={props.env} onChange={props.onEnvChange} />
        {props.isAuthed && (
          <>
            <span style={{ width: 1, height: 24, background: '#e5e7eb' }} />
            <ContextPicker
              eyebrow="Org"
              items={props.organizations.map((o) => ({ id: o.id, label: pickName(o) }))}
              value={props.organizationId}
              onChange={props.onOrganizationIdChange}
              placeholder={props.discovering ? 'Loading…' : '— pick organization —'}
              loading={props.discovering}
            />
            {props.workspaces.length > 0 && (
              <ContextPicker
                eyebrow="Workspace"
                items={props.workspaces.map((w) => ({ id: w.id, label: pickName(w) }))}
                value={props.workspaceId}
                onChange={props.onWorkspaceIdChange}
                placeholder="Default workspace"
              />
            )}
          </>
        )}
      </div>
      <div className="lp-flex lp-items-center lp-gap-2 lp-shrink-0">
        {props.isAuthed && props.email && (
          <div className="lp-flex lp-items-center lp-gap-2 lp-text-sm">
            <div
              className="lp-w-7 lp-h-7 lp-rounded-full lp-text-white lp-text-[10px] lp-font-bold lp-flex lp-items-center lp-justify-center"
              style={{
                background: 'linear-gradient(135deg, #6256ff, #7c3aed)',
                boxShadow: '0 6px 14px rgba(98, 86, 255, 0.28)',
              }}
            >
              {props.email.slice(0, 2).toUpperCase()}
            </div>
            <span className="lp-font-medium lp-hidden md:lp-inline lp-text-xs" style={{ color: '#334155' }}>
              {props.email}
            </span>
            <button
              onClick={props.onSignOut}
              className="lp-text-[11px] lp-px-2 lp-py-1 lp-rounded-md"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.color = '#0f172a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function pickName(item: { id: string; name?: string }): string {
  return item.name && item.name.trim() ? item.name : item.id;
}

// ============================================================================
// Context picker (org / workspace dropdown)
// ============================================================================

function ContextPicker({
  eyebrow,
  items,
  value,
  onChange,
  placeholder,
  loading,
}: {
  eyebrow: string;
  items: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = items.find((i) => i.id === value);
  const filtered = query
    ? items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase()) || i.id.toLowerCase().includes(query.toLowerCase()))
    : items;

  return (
    <div ref={ref} className="lp-relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="lp-flex lp-items-center lp-gap-2 lp-pl-2.5 lp-pr-2 lp-py-1 lp-text-xs lp-transition-colors"
        style={{
          borderRadius: 8,
          background: selected ? '#f1f0ff' : '#f1f5f9',
          border: `1px solid ${selected ? '#cbc4ff' : 'transparent'}`,
          color: '#1e293b',
          maxWidth: 280,
        }}
      >
        <span
          className="lp-text-[10px] lp-font-bold lp-uppercase"
          style={{ letterSpacing: '0.1em', color: selected ? '#4030d4' : '#64748b' }}
        >
          {eyebrow}
        </span>
        <span className="lp-truncate lp-font-medium" style={{ color: selected ? '#0f172a' : '#94a3b8', maxWidth: 180 }}>
          {loading ? 'Loading…' : selected?.label || placeholder || '—'}
        </span>
        <svg className="lp-w-3 lp-h-3 lp-shrink-0" style={{ color: '#94a3b8' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          className="lp-absolute lp-z-50 lp-mt-1.5 lp-left-0 lp-w-72 lp-py-1.5"
          style={{
            background: '#ffffff',
            borderRadius: 12,
            boxShadow: '0 18px 40px rgba(15,23,42,0.14)',
            border: '1px solid #e5e7eb',
            maxHeight: 320,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {items.length > 5 && (
            <div className="lp-px-2 lp-pb-1.5">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Filter ${items.length}…`}
                className="lp-w-full lp-text-xs lp-px-2.5 lp-py-1.5 lp-rounded-md"
                style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#0f172a', outline: 'none' }}
              />
            </div>
          )}
          <div className="lp-overflow-y-auto" style={{ maxHeight: 280 }}>
            {filtered.length === 0 && (
              <div className="lp-px-3 lp-py-3 lp-text-xs" style={{ color: '#94a3b8' }}>
                No matches
              </div>
            )}
            {filtered.map((item) => {
              const isActive = item.id === value;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onChange(item.id);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="lp-w-full lp-text-left lp-px-3 lp-py-2 lp-flex lp-items-center lp-gap-2 lp-transition-colors"
                  style={{
                    background: isActive ? '#f8f7ff' : 'transparent',
                    color: isActive ? '#4030d4' : '#334155',
                  }}
                  onMouseEnter={(el) => {
                    if (!isActive) el.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(el) => {
                    if (!isActive) el.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span className="lp-flex-1 lp-min-w-0">
                    <div className="lp-text-[12.5px] lp-font-medium lp-truncate">{item.label}</div>
                    <div className="lp-text-[10.5px] lp-font-mono lp-truncate" style={{ color: '#94a3b8' }}>
                      {item.id}
                    </div>
                  </span>
                  {isActive && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div
      className="lp-inline-flex lp-p-0.5 lp-gap-0.5"
      style={{ background: '#f1f5f9', borderRadius: 8 }}
    >
      {(['preview', 'live'] as Mode[]).map((m) => {
        const active = m === mode;
        const isLive = m === 'live';
        return (
          <button
            key={m}
            onClick={() => onChange(m)}
            className="lp-text-xs lp-font-semibold lp-px-3 lp-py-1.5 lp-flex lp-items-center lp-gap-1.5 lp-transition-all"
            style={{
              borderRadius: 6,
              background: active ? (isLive ? 'linear-gradient(135deg, #6256ff, #7c3aed)' : '#ffffff') : 'transparent',
              color: active ? (isLive ? '#ffffff' : '#0f172a') : '#64748b',
              boxShadow: active ? (isLive ? '0 6px 14px rgba(98,86,255,0.28)' : '0 1px 2px rgba(15,23,42,0.06)') : 'none',
            }}
          >
            {isLive && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 9999,
                  background: active ? '#ffffff' : '#6256ff',
                }}
              />
            )}
            {m === 'preview' ? 'Preview' : 'Live'}
          </button>
        );
      })}
    </div>
  );
}

function EnvSwitcher({ env, onChange }: { env: Env; onChange: (e: Env) => void }) {
  const [open, setOpen] = useState(false);
  const labels: Record<Env, string> = {
    production: 'Production',
    staging: 'Staging',
    development: 'Dev',
  };
  const dotColor: Record<Env, string> = {
    production: '#10b981',
    staging: '#f59e0b',
    development: '#0ea5e9',
  };
  return (
    <div className="lp-relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="lp-flex lp-items-center lp-gap-2 lp-px-3 lp-py-1.5 lp-text-xs lp-font-semibold lp-transition-colors"
        style={{ background: '#f1f5f9', color: '#334155', borderRadius: 8 }}
        onMouseEnter={(e) => (e.currentTarget.style.background = '#e5e7eb')}
        onMouseLeave={(e) => (e.currentTarget.style.background = '#f1f5f9')}
      >
        <span style={{ width: 6, height: 6, borderRadius: 9999, background: dotColor[env] }} />
        {labels[env]}
        <svg className="lp-w-3 lp-h-3" style={{ color: '#94a3b8' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <>
          <div className="lp-fixed lp-inset-0 lp-z-40" onClick={() => setOpen(false)} />
          <div
            className="lp-absolute lp-mt-1.5 lp-left-0 lp-z-50 lp-w-44 lp-py-1.5"
            style={{ background: '#ffffff', borderRadius: 12, boxShadow: '0 12px 30px rgba(15,23,42,0.12)', border: '1px solid #e5e7eb' }}
          >
            {(Object.keys(labels) as Env[]).map((e) => (
              <button
                key={e}
                onClick={() => {
                  onChange(e);
                  setOpen(false);
                }}
                className="lp-w-full lp-text-left lp-px-3 lp-py-2 lp-text-xs lp-font-medium lp-flex lp-items-center lp-gap-2 lp-transition-colors"
                style={{
                  color: env === e ? '#4030d4' : '#334155',
                  background: env === e ? '#f8f7ff' : 'transparent',
                }}
                onMouseEnter={(el) => {
                  if (env !== e) el.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(el) => {
                  if (env !== e) el.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 9999, background: dotColor[e] }} />
                {labels[e]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
